import React from 'react';
import { connect } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import PrimaryNavBar from 'commonComponents/PrimaryNavBar';
import PrimaryNavBarOld from 'commonComponents/PrimaryNavBarOld';
import { isEmpty } from 'lodash';
import * as actionCreators from './SideBar.actions';
import EmptyStateSideBar from './EmptyStateSideBar.component';

/**
 * SideBar Component - Renders PrimaryNavBar
 */
const SideBar = (props) => {
  const {
    msgsCount,
    authorize,
    isShareToken,
    userDetails,
    mfaResponseReceived,
    mfaConfig,
    showNewUI,
    initiatorDetails,
    isImpersonated,
    defaultApp: { appId: defaultAppId },
    actions: {
      navigateAction,
    },
    shouldSidebarRender,
    shouldHeaderRender,
    featureFlagsLoaded,
    userProfileError,
    evoneBannerVisible,
    thirdPartyAppsFetched,
  } = props;
  const { url: matchURL } = useRouteMatch();

  /**
   * navigates new route
   * @param {String} url - new route to load selected application
   */
  const navigate = (url) => {
    navigateAction({ navigateTo: `${matchURL}${url}` });
  };

  // Show empty state sidebar when there's a user profile error
  if (userProfileError) {
    return (
      <EmptyStateSideBar
        showNewUI={showNewUI}
        shouldSidebarRender={shouldSidebarRender}
        shouldHeaderRender={shouldHeaderRender}
        userProfileError={userProfileError}
      />
    );
  }

  // Guest (SHARE token) with no apps — show minimal sidebar so layout stays consistent.
  // Old UI: avatar with email + logout rendered by PrimaryNavBarOld (isGuestUser from Redux).
  // New UI: avatar with email + logout rendered by the Header component. #genai
  if (authorize && isShareToken && (!mfaConfig || mfaConfig.length === 0)) {
    return (
      <EmptyStateSideBar
        showNewUI={showNewUI}
        shouldSidebarRender={shouldSidebarRender}
        shouldHeaderRender={shouldHeaderRender}
        userProfileError={false}
        userDetails={userDetails}
      />
    );
  }

  // #genai wait for third-party entitled-apps so Evone banner visibility is known before first paint (no banner pop-in)
  if (authorize && mfaResponseReceived && featureFlagsLoaded && thirdPartyAppsFetched) {
    if (showNewUI && mfaConfig.length === 1) {
      const pathname = (window.location && window.location.pathname) || '/';
      const appIdInUrl = pathname.split('/')[1] || '';
      if (isEmpty(appIdInUrl)) {
        navigate(get(mfaConfig[0], 'appId', ''));
      }
      return null;
    }

    const primaryNavBarProps = {
      apps: mfaConfig,
      userDetails,
      handleNavChange: navigate,
      defaultAppId,
      msgsCount,
      show: shouldSidebarRender,
      shouldHeaderRender,
      initiatorDetails,
      isImpersonated,
      userProfileError,
    };
    return (
      showNewUI
        ? <PrimaryNavBar {...primaryNavBarProps} />
        : <PrimaryNavBarOld {...primaryNavBarProps} evoneBannerVisible={evoneBannerVisible} />
    );
  }
  return null;
};

/**
 * selects required data from redux store
 * @param {Object} state - redux store object
 */
const selector = (state) => ({
  mfaConfig: state.SideBar.mfaConfig,
  defaultApp: state.SideBar.defaultApp,
  mfaResponseReceived: state.SideBar.mfaResponseReceived,
  authorize: state.Authorize.authorize,
  isShareToken: Boolean(state.Authorize?.isShareToken),
  userDetails: state.SideBar.userDetails,
  shouldSidebarRender: state.SideBar.shouldSidebarRender,
  shouldHeaderRender: state.SideBar.shouldHeaderRender,
  showNewUI: state.SideBar.showNewUI,
  msgsCount: state.SideBar.msgsCount,
  featureFlagsLoaded: state.SideBar.featureFlagsLoaded,
  userProfileError: state.SideBar.userProfileError,
  initiatorDetails: state.SideBar.initiatorDetails,
  isImpersonated: state.SideBar.isImpersonated,
  evoneBannerVisible: state.SideBar.evoneBannerVisible,
  thirdPartyAppsFetched: state.UserEntitledApps?.thirdPartyAppsFetched ?? false,
});

/**
 * returns a set of actions after binding dispatch
 * @param {Function} dispatch - redux dispatch function
 */
const actions = (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
});

SideBar.propTypes = {
  /**
   * set of actions passed by actions function
   */
  actions: PropTypes.shape({
    getMfaConfigAction: PropTypes.func.isRequired,
    getUserDetails: PropTypes.func.isRequired,
    getLoaders: PropTypes.func.isRequired,
    navigateAction: PropTypes.func.isRequired,
  }).isRequired,

  /**
   * The list of micro applications.
   * @param {{appId: String, rootUrl: String, prefetch: Boolean, name: String}[]} mfaConfig
   */
  mfaConfig: PropTypes.arrayOf(
    PropTypes.shape({
      appId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      rootUrl: PropTypes.string.isRequired,
      prefetch: PropTypes.bool,
    }),
  ),

  /**
   * default micro application to load applications.
   * @param {{appId: String, rootUrl: String, prefetch: Boolean, name: String}} defaultApp
   */
  defaultApp: PropTypes.shape({
    appId: PropTypes.string,
    name: PropTypes.string,
    rootUrl: PropTypes.string,
    prefetch: PropTypes.bool,
  }),

  /**
   * store value containing the status of entitlement service
   * @param {Boolean} mfaResponseReceived
   */
  mfaResponseReceived: PropTypes.bool,

  /**
   * store value containing the status of authentication
   * @param {Boolean} authorize
   */
  authorize: PropTypes.bool,

  /**
   * Details of the logged in user
   * @property {Object} userDetails
   */
  userDetails: PropTypes.shape(),

  /**
   * Details of the initiator when impersonation is done
   * @property {Object} initiatorDetails
   */
  initiatorDetails: PropTypes.shape(),

  /**
   * store value indicating whether the user is impersonated or not
   * @property {Boolean} isImpersonated
   */
  isImpersonated: PropTypes.bool,

  /**
   * Current address bar url
   * @property {Boolean} shouldSidebarRender
   */
  shouldSidebarRender: PropTypes.bool,

  /**
   * @property {Boolean} shouldHeaderRender
   */
  shouldHeaderRender: PropTypes.bool,

  /**
   * store value containing the status of authentication
   * @param {Number} msgsCount
   */
  msgsCount: PropTypes.number,

  /**
   * show New UI
   * @property {Boolean} showNewUI
   */
  showNewUI: PropTypes.bool.isRequired,

  /**
   * launchdarkly feature flags loaded
   * @property {Boolean} featureFlagsLoaded
   */
  featureFlagsLoaded: PropTypes.bool.isRequired,

  /**
   * Third-party entitled-apps fetch finished (gates sidebar until Evone eligibility is known). #genai
   * @property {Boolean} thirdPartyAppsFetched
   */
  thirdPartyAppsFetched: PropTypes.bool,

  /**
   * store value containing the status of user profile error
   * @param {Boolean} userProfileError
   */
  userProfileError: PropTypes.bool,

  /**
   * True when current auth token is SHARE (guest). From Redux, not localStorage. #genai
   * @param {Boolean} isShareToken
   */
  isShareToken: PropTypes.bool,

  /** #genai */
  evoneBannerVisible: PropTypes.bool,
};

SideBar.defaultProps = {
  mfaConfig: [],
  defaultApp: {
    appId: '',
  },
  mfaResponseReceived: false,
  authorize: false,
  isShareToken: false,
  userDetails: {},
  shouldSidebarRender: true,
  shouldHeaderRender: true,
  msgsCount: 0,
  initiatorDetails: {},
  isImpersonated: false,
  userProfileError: false,
  evoneBannerVisible: false,
  thirdPartyAppsFetched: false,
};
export { SideBar };
export default connect(selector, actions)(SideBar);
